import { useEffect } from "react";
import { ORDER_TYPES } from "../../Enums/orderTypes";
import {
  DATE_FORMATS,
  DATE_RANGE_TYPE,
  formatDate,
} from "../../Util/dateUtils";

const timeFrameOptions = [
  { value: DATE_RANGE_TYPE.LAST_24_HOURS, label: "Today" },
  { value: DATE_RANGE_TYPE.LAST_7_DAYS, label: "This week" },
  { value: DATE_RANGE_TYPE.PREVIOUS_7_DAYS, label: "Last week" },
  { value: DATE_RANGE_TYPE.LAST_30_DAYS, label: "This month" },
];

const orderRatingOptions = [
  { value: "ALL", label: "Order Ratings" },
  { value: "5", label: "5 Star" },
  { value: "4", label: "4 Star" },
  { value: "3", label: "3 Star" },
  { value: "2", label: "2 Star" },
  { value: "1", label: "1 Star" },
];

const driverRatingOptions = [
  { value: "ALL", label: "Driver Ratings" },
  { value: "5", label: "5 Star" },
  { value: "4", label: "4 Star" },
  { value: "3", label: "3 Star" },
  { value: "2", label: "2 Star" },
  { value: "1", label: "1 Star" },
];

const orderTypeOptions = [
  { value: "ALL", label: "All Orders" },
  { value: ORDER_TYPES.DELIVERY, label: "Delivery Orders" },
  { value: ORDER_TYPES.PICKUP, label: "Pickup Orders" },
  { value: ORDER_TYPES.DINE_IN, label: "Dine-in Orders" },
];

const sortByOptions = [
  { value: "TIME,DSC", label: "Sort by: Newest" },
  { value: "TIME,ASC", label: "Sort by: Oldest" },
  { value: "ORDER_RATING,DSC", label: "Sort by: Highest" },
  { value: "ORDER_RATING,ASC", label: "Sort by: Lowest" },
];

export const responseViewOptions = {
  FULL: "FULL",
  START: "START",
};

const initialState = {
  allReviews: null,
  allCompanies: null,
  companyConfigs: null,
  reviewChatRooms: null,

  timeFrame: DATE_RANGE_TYPE.LAST_7_DAYS,
  orderRating: "ALL",
  driverRating: "ALL",
  orderType: "ALL",
  sortBy: "TIME",
  sortOrder: "DSC",
  sort: "TIME,DSC",
  companyIds: [],
  pageSize: 20,
  pageNumber: 1,

  processedReviews: [],
  reviewRoomKeys: {},
  totalPages: 1,
  filteredCount: 0,

  reviewOpened: false,
  responseView: null,
  reviewOrderDetails: null,
  reviewRoomId: null,

  refetchReviewRoomInfo: null,
};

const actions = {
  SET_ALL_REVIEWS: "SET_ALL_REVIEWS",
  SET_ALL_COMPANIES: "SET_ALL_COMPANIES",
  SET_REVIEW_CHAT_ROOMS: "SET_REVIEW_CHAT_ROOMS",
  SET_COMPANY_CONFIGS: "SET_COMPANY_CONFIGS",
  SET_PLANS: "SET_PLANS",
  SET_TIME_FRAME: "SET_TIME_FRAME",
  SET_ORDER_RATING: "SET_ORDER_RATING",
  SET_DRIVER_RATING: "SET_DRIVER_RATING",
  SET_ORDER_TYPE: "SET_ORDER_TYPE",
  SET_SORT: "SET_SORT",
  SET_COMPANY_IDS: "SET_COMPANY_IDS",
  SET_PAGE_NUMBER: "SET_PAGE_NUMBER",
  SET_PAGE_SIZE: "SET_PAGE_SIZE",
  OPEN_RESPONSE_VIEW: "OPEN_RESPONSE_VIEW",
  CLOSE_RESPONSE_VIEW: "CLOSE_RESPONSE_VIEW",
  // INITIATE_RESPONSE: "INITIATE_RESPONSE",
};

const useSetter = (state, reducerDispatch, action) => {
  useEffect(() => {
    if (state === null) return;
    reducerDispatch({
      type: action,
      payload: state,
    });
  }, [state, reducerDispatch, action]);
}


const fillWithProcessedReviews = (state) => {
  let reviews = state.allReviews?.getAllReviews() ?? [];
  if (state.companyIds.length > 0) {
    reviews = reviews.filter((review) =>
      state.companyIds.includes(review.companyId)
    );
  }
  if (state.orderRating !== "ALL") {
    reviews = reviews.filter(
      (review) => review.orderRating === Number(state.orderRating)
    );
  }
  if (state.driverRating !== "ALL") {
    reviews = reviews.filter(
      (review) => review.driverRating === Number(state.driverRating) && review.orderType === ORDER_TYPES.DELIVERY
    );
  }
  if (state.orderType !== "ALL") {
    reviews = reviews.filter((review) => review.orderType === state.orderType);
  }
  const orderMultiplier = state.sortOrder === "ASC" ? 1 : -1;
  if (state.sortBy === "TIME") {
    reviews = reviews.sort(
      (a, b) => (a.reviewTime - b.reviewTime) * orderMultiplier
    );
  } else if (state.sortBy === "ORDER_RATING") {
    reviews = reviews.sort(
      (a, b) => (a.orderRating - b.orderRating) * orderMultiplier
    );
  }

  const paginatedReviews = reviews.slice(
    (state.pageNumber - 1) * state.pageSize,
    state.pageNumber * state.pageSize
  );

  const orderIds = paginatedReviews
    .map((review) => review.getOrderId()).sort();

  return {
    ...state,
    processedReviews: mapToUiRevies(paginatedReviews, state.allCompanies, state.reviewChatRooms, state.companyConfigs, state.plans),
    reviewRoomKeys: getReviewRoomKeys(paginatedReviews),
    totalPages: Math.ceil(reviews.length / state.pageSize),
    filteredCount: reviews.length,
  };
};

const mapToUiRevies = (reviews, allCompanies, reviewChatRooms, companyConfigs, plans) => {
  return reviews.map((review) => {
    const company = allCompanies?.getAccountDetailsByCompanyId(
      review.getCompanyId()
    );
    const isMerchant = companyConfigs?.isMerchant(review.getCompanyId()) ?? false;
    const canOfferReviewResponse = plans?.canOfferReviewResponse(company?.currentPlan ?? "") ?? false;
    
    const reviewChatRoom = review.getOrderType() === ORDER_TYPES.DINE_IN ? 
        reviewChatRooms?.getReviewRoomByReviewId(review.getReviewId()) : 
        reviewChatRooms?.getReviewRoomByOrderId(review.getOrderId());

    return {
      key: review.getReviewId(),
      orderId: review.getOrderId(),
      orderType: review.getOrderType(),
      customerName: review.getCustomerName(),
      carrierName: review.getCarrierName(),
      reviewTimeFormatted: formatDate(
        review.getReviewTime(),
        DATE_FORMATS.MMM_DDc_YYYY
      ),
      reviewTimeRaw: review.getReviewTime(),
      orderNumber: review.getOrderNumber(),
      customerPhoneNumber: review.getCustomerPhoneNumber() || "N/A",
      orderRating: review.getOrderRating(),
      driverRating: review.getDriverRating(),
      serviceRating: review.getServiceRating(),
      reviewText: review.getReviewText(),
      reviewId: review.getReviewId(),
      companyId: review.getCompanyId(),
      companyName: company?.companyName ?? "",
      hasReviewChatRoom: reviewChatRoom?.roomId !== -1,
      chatRoomId: reviewChatRoom?.roomId,
      chatRoomHasFirstMessage: !!(reviewChatRoom?.firstMessage),
      chatRoomFirstMessage: reviewChatRoom && reviewChatRoom?.firstMessage,
      chatRoomFirstMessageSentAt: reviewChatRoom?.firstMessageSentAt,
      customerRerferenceId: reviewChatRoom?.customerReferenceId,
      planSupportsReviewChat: canOfferReviewResponse,
      hasReviewChat: isMerchant,
    };
  });
};

const getReviewRoomKeys = (reviews) => {
  const roomKeys = {
    orderIds: [],
    reviewIds: [],
  }
  return reviews.reduce((acc, review) => {
    if (review.getOrderType() === ORDER_TYPES.DINE_IN) {
      acc.reviewIds.push(review.getReviewId());
    } else {
      acc.orderIds.push(review.getOrderId());
    }
    return acc;
  }, roomKeys);
};

const getOrderDeailsForResponse = (review) => {
  return {
      orderId: review.orderId,
      reviewId: review.reviewId,
      orderNumber: review.orderNumber,
      reviewTime: formatDate(review.reviewTimeRaw, DATE_FORMATS.ISO),
      foodRating: review.orderRating,
      feedbackDetails: review.reviewText,
      driverRating: review.driverRating,
      serviceRating: review.serviceRating,
      orderType: review.orderType,
      companyId: review.companyId,
      customerPhone: review.customerPhoneNumber,
      customerName: review.customerName,
      companyInfo : {
        name: review.companyName,
      }
  };
};

const reducer = (state = initialState, action) => {
  switch (action.type) {
    case actions.SET_ALL_REVIEWS:
      return fillWithProcessedReviews({
        ...state,
        pageNumber: 1,
        allReviews: action.payload,
      });

    case actions.SET_ALL_COMPANIES:
      return fillWithProcessedReviews({
        ...state,
        allCompanies: action.payload,
      });

    case actions.SET_REVIEW_CHAT_ROOMS:
      return fillWithProcessedReviews({
        ...state,
        reviewChatRooms: action.payload,
      });

    case actions.SET_COMPANY_CONFIGS:
      return fillWithProcessedReviews({
        ...state,
        companyConfigs: action.payload,
      });

    case actions.SET_PLANS:
      return fillWithProcessedReviews({
        ...state,
        plans: action.payload,
      });

    case actions.SET_TIME_FRAME:
      return fillWithProcessedReviews({
        ...state,
        pageNumber: 1,
        timeFrame: action.timeFrame,
      });

    case actions.SET_ORDER_RATING:
      return fillWithProcessedReviews({
        ...state,
        pageNumber: 1,
        orderRating: action.orderRating,
      });

    case actions.SET_DRIVER_RATING:
      return fillWithProcessedReviews({
        ...state,
        pageNumber: 1,
        driverRating: action.driverRating,
      });

    case actions.SET_ORDER_TYPE:
      return fillWithProcessedReviews({
        ...state,
        pageNumber: 1,
        orderType: action.orderType,
      });

    case actions.SET_SORT:
      const [sortBy, sortOrder] = action.sort.split(",");
      return fillWithProcessedReviews({
        ...state,
        sortBy,
        sortOrder,
        sort: sortBy + "," + sortOrder,
      });

    case actions.SET_COMPANY_IDS:
      return fillWithProcessedReviews({
        ...state,
        pageNumber: 1,
        companyIds: action.companyIds ?? [],
      });

    case actions.SET_PAGE_NUMBER:
      return fillWithProcessedReviews({
        ...state,
        pageNumber: action.pageNumber,
      });

    case actions.SET_PAGE_SIZE:
      return fillWithProcessedReviews({
        ...state,
        pageSize: action.pageSize,
      });

    case actions.OPEN_RESPONSE_VIEW: {
      if (state.reviewChatRooms === null) {
        return state;
      }

      const review = state.processedReviews.find((review) => review.key === action.key);
      const view = responseViewOptions.FULL;
      return {
        ...state,
        reviewOpened: true,
        responseView: view,
        reviewOrderDetails: getOrderDeailsForResponse(review),
        reviewRoomId: review.chatRoomId,
        targetReferenceId: review.customerRerferenceId,
      };
    }

    case actions.CLOSE_RESPONSE_VIEW:
      return {
        ...state,
        reviewOpened: false,
        responseView: null,
        reviewOrderDetails: null,
        reviewRoomId: null,
        targetReferenceId: null,
        ...((state.reviewRoomId === -1  || state.reviewRoomId === null) && { refetchReviewRoomInfo: new Date().getTime() }),
      };

    // case actions.INITIATE_RESPONSE: {
    //   return {
    //     ...state,
    //     reviewOpened: false,
    //     responseView: null,
    //     reviewOrderDetails: null,
    //     reviewRoomId: null,
    //     refetchReviewRoomInfo: new Date().getTime(),
    //   };
    // }

    default:
      return state;
  }
};

export {
  actions,
  initialState,
  reducer,
  timeFrameOptions,
  orderRatingOptions,
  driverRatingOptions,
  orderTypeOptions,
  sortByOptions,
  useSetter,
};
